/**
 * MobileAgendaView — standard "Prev · Today · Next" agenda header.
 *
 * Reused by Rota, MyShifts, MarChart, Calendar in later batches. Owns
 * only the presentation; the caller drives the actual date state.
 *
 *   <MobileAgendaView
 *     date={iso}
 *     onPrev={() => ...} onNext={() => ...} onToday={() => ...}
 *     label="Wednesday · 05 Feb"
 *     children={<agendaList />}
 *   />
 */
import React from "react";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";

export default function MobileAgendaView({
  label,
  onPrev,
  onNext,
  onToday,
  isToday = false,
  children,
  className,
  testid = "mobile-agenda",
}) {
  return (
    <div className={cn("w-full", className)} data-testid={testid}>
      <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-2">
        <button
          type="button"
          onClick={onPrev}
          data-testid={`${testid}-prev`}
          aria-label="Previous day"
          className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
          disabled={!onPrev}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={onToday}
          data-testid={`${testid}-today`}
          className={cn(
            "flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-semibold",
            isToday
              ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300"
              : "text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800",
          )}
        >
          <CalendarClock className="w-4 h-4" />
          <span className="truncate">{label}</span>
        </button>

        <button
          type="button"
          onClick={onNext}
          data-testid={`${testid}-next`}
          aria-label="Next day"
          className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
          disabled={!onNext}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      {children && (
        <div className="mt-3" data-testid={`${testid}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}
